import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { labelClass, inputClass } from '@/components/ui/inputStyles';
import { generateWesWebhookSecret } from '@/lib/webhookSecret';
import { buildRobotSiteDisplayRows, type RobotSiteRow } from '@/lib/robotSiteTree';

type MpResp = {
  ok: boolean;
  miniprogram?: {
    id: number;
    name: string;
    site_ids: number[];
    entry_site_options?: Array<{ id: number; site_name: string }>;
    android_robot_api_key?: string;
    types?: Array<{ key: string; label: string }>;
  };
};

type RobotInheritMeta = {
  parent_site_id?: number | null;
  parent_site_name?: string;
  robot_inherit_parent?: boolean;
  using_parent_minipro_settings?: boolean;
};

type RobotSettingsSource =
  | 'miniprogram_override'
  | 'miniprogram_inherit_parent'
  | 'miniprogram_default'
  | 'site'
  | string;

type RobotSummary = {
  site_id: number;
  parent_site_id?: number | null;
  robot_module_enabled: boolean;
  forward_strategy: 'auto' | 'curated';
  forward_daily_cap: number;
  /** null=全部信息类型自动转发 */
  forward_type_keys?: string[] | null;
  forward_task_count: number;
  forward_task_count_aggregate?: number;
  inherit?: RobotInheritMeta;
  robot_settings_source?: RobotSettingsSource;
};

type BatchSummaryResp = { ok: boolean; items: RobotSummary[] };

export function RobotMiniprogramDetailPage() {
  const { mpId: mpParam } = useParams();
  const mpId = Math.max(0, parseInt(mpParam || '0', 10) || 0);

  const [mpName, setMpName] = useState('');
  const [sites, setSites] = useState<RobotSiteRow[]>([]);
  const [androidKey, setAndroidKey] = useState('');
  const [keySaving, setKeySaving] = useState(false);
  const [keyMsg, setKeyMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [summaryById, setSummaryById] = useState<Record<number, RobotSummary>>({});
  const [strategySite, setStrategySite] = useState<RobotSiteRow | null>(null);
  const [strategyChoice, setStrategyChoice] = useState<'auto' | 'curated'>('curated');
  const [strategyDailyCap, setStrategyDailyCap] = useState(10);
  const [strategyForwardAllTypes, setStrategyForwardAllTypes] = useState(true);
  const [strategyForwardTypeKeys, setStrategyForwardTypeKeys] = useState<string[]>([]);
  const [mpTypes, setMpTypes] = useState<Array<{ key: string; label: string }>>([]);
  const [strategySaving, setStrategySaving] = useState(false);

  const load = useCallback(async () => {
    if (mpId < 1) return;
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<MpResp>('get_miniprogram', { method: 'GET', query: { id: mpId } });
      if (!r.ok || !r.miniprogram) {
        setMsg('无法加载小程序');
        return;
      }
      const m = r.miniprogram;
      setMpName(m.name);
      setAndroidKey(String(m.android_robot_api_key ?? ''));
      setMpTypes(Array.isArray(m.types) ? m.types.filter((t) => t && t.key) : []);

      const opts = Array.isArray(m.entry_site_options) ? m.entry_site_options : [];
      const scopeIds = new Set<number>(
        opts.length > 0
          ? opts.map((o) => Number(o.id)).filter((x) => x > 0)
          : (m.site_ids || []).map((x) => Number(x)).filter((x) => x > 0),
      );

      const ls = await apiFetch<{ ok: boolean; items: RobotSiteRow[] }>('list_sites', {
        method: 'GET',
        query: { page: 1, per_page: 500, only_roots: 0 },
      });
      let filtered: RobotSiteRow[] = [];
      if (ls.ok) {
        filtered = ls.items.filter((s) => scopeIds.has(s.id));
        const have = new Set(filtered.map((s) => s.id));
        for (const o of opts) {
          const oid = Number(o.id);
          if (oid > 0 && !have.has(oid)) {
            filtered.push({
              id: oid,
              site_code: '',
              site_name: String(o.site_name ?? `站点 #${oid}`),
              parent_site_id: null,
            });
          }
        }
      } else if (opts.length > 0) {
        filtered = opts.map((o) => ({
          id: Number(o.id),
          site_code: '',
          site_name: String(o.site_name ?? `站点 #${o.id}`),
          parent_site_id: null,
        }));
      }
      setSites(filtered);

      const ids = filtered.map((s) => s.id);
      if (ids.length === 0) {
        setSummaryById({});
        return;
      }
      const b = await apiFetch<BatchSummaryResp>('batch_robot_site_summary', {
        method: 'POST',
        body: JSON.stringify({ site_ids: ids, miniprogram_id: mpId }),
      });
      if (!b.ok) {
        setMsg('加载站点机器人概览失败');
        return;
      }
      const map: Record<number, RobotSummary> = {};
      for (const it of b.items) {
        const cap =
          typeof it.forward_daily_cap === 'number' && !Number.isNaN(it.forward_daily_cap)
            ? Math.min(500, Math.max(1, Math.round(it.forward_daily_cap)))
            : 10;
        map[it.site_id] = { ...it, forward_daily_cap: cap };
      }
      setSummaryById(map);
    } finally {
      setLoading(false);
    }
  }, [mpId]);

  useEffect(() => {
    void load();
  }, [load]);

  const siteDisplayRows = useMemo(() => buildRobotSiteDisplayRows(sites), [sites]);

  function fillRandomAndroidRobotKey() {
    setAndroidKey(generateWesWebhookSecret());
    setKeyMsg('已填入随机密钥，请点击「保存密钥」写入服务端');
  }

  async function saveAndroidKey() {
    if (mpId < 1) return;
    setKeySaving(true);
    setKeyMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram_android_robot_key', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: mpId, android_robot_api_key: androidKey }),
      });
      setKeyMsg(r.ok ? '已保存' : r.err || '保存失败');
      if (r.ok) await load();
    } finally {
      setKeySaving(false);
    }
  }

  async function toggleModule(siteId: number, enabled: boolean) {
    setMsg('');
    const cur = summaryById[siteId];
    const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram_site_robot_settings', {
      method: 'POST',
      body: JSON.stringify({
        miniprogram_id: mpId,
        site_id: siteId,
        robot_module_enabled: enabled,
        forward_strategy: cur?.forward_strategy ?? 'curated',
        forward_daily_cap: cur?.forward_daily_cap ?? 10,
        forward_type_keys: cur?.forward_type_keys ?? null,
      }),
    });
    if (!r.ok) {
      setMsg(r.err || '保存失败');
      return;
    }
    await load();
  }

  function openStrategy(s: RobotSiteRow) {
    const cur = summaryById[s.id]?.forward_strategy ?? 'curated';
    setStrategyChoice(cur === 'auto' ? 'auto' : 'curated');
    const cap = summaryById[s.id]?.forward_daily_cap;
    setStrategyDailyCap(typeof cap === 'number' && !Number.isNaN(cap) ? Math.min(500, Math.max(1, Math.round(cap))) : 10);
    const fwdKeys = summaryById[s.id]?.forward_type_keys;
    if (fwdKeys === null || fwdKeys === undefined) {
      setStrategyForwardAllTypes(true);
      setStrategyForwardTypeKeys([]);
    } else {
      setStrategyForwardAllTypes(false);
      setStrategyForwardTypeKeys(Array.isArray(fwdKeys) ? [...fwdKeys] : []);
    }
    setStrategySite(s);
  }

  function toggleStrategyForwardType(key: string) {
    setStrategyForwardAllTypes(false);
    setStrategyForwardTypeKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function setStrategyForwardAllTypesEnabled(all: boolean) {
    setStrategyForwardAllTypes(all);
    if (all) {
      setStrategyForwardTypeKeys([]);
    }
  }

  async function saveStrategy() {
    if (!strategySite) return;
    setStrategySaving(true);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram_site_robot_settings', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: strategySite.id,
          forward_strategy: strategyChoice,
          forward_daily_cap: Math.min(500, Math.max(1, Math.round(strategyDailyCap))),
          forward_type_keys: strategyForwardAllTypes ? null : strategyForwardTypeKeys,
        }),
      });
      if (!r.ok) {
        setMsg(r.err || '保存失败');
        return;
      }
      setStrategySite(null);
      await load();
    } finally {
      setStrategySaving(false);
    }
  }

  async function restoreRobotInherit(siteId: number) {
    setMsg('');
    const r = await apiFetch<{ ok: boolean; err?: string }>('restore_site_settings_parent_inherit', {
      method: 'POST',
      body: JSON.stringify({ site_id: siteId, scope: 'robot' }),
    });
    if (!r.ok) {
      setMsg(r.err || '恢复继承失败');
      return;
    }
    setStrategySite(null);
    await load();
  }

  if (mpId < 1) {
    return <p className="text-sm text-rose-700">无效的小程序</p>;
  }

  const strategyInherit = strategySite ? summaryById[strategySite.id]?.inherit : undefined;

  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-500">
        <Link to="/miniprograms" className="rounded-md hover:bg-slate-100 hover:text-teal-800">
          小程序管理
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <span className="font-medium text-slate-800">{mpName || `小程序 #${mpId}`}</span>
      </nav>

      <PageHeader
        title={mpName ? `站点转发配置 · ${mpName}` : '站点转发配置'}
        description={`小程序 #${mpId}：各站点的转发开关、策略与队列均仅在本小程序内生效；同一站点在其他小程序中需单独配置。二级站点可继承本小程序内一级站点的策略。`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to={`/robot-management?mp_id=${mpId}`}>
              <Button type="button" variant="primary">
                机器人管理
              </Button>
            </Link>
            <Button type="button" variant="secondary" onClick={() => void load()} disabled={loading}>
              刷新
            </Button>
            <Link to="/miniprograms">
              <Button type="button" variant="secondary">
                返回小程序管理
              </Button>
            </Link>
          </div>
        }
      />
      {msg ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{msg}</p> : null}

      <Card title="小程序 Android 机器人密钥">
        <p className="text-sm text-slate-600">
          Android 端拉取转发任务、上报完成状态时需携带此密钥。留空则拒绝机器人接口请求；建议定期更换并同步到客户端配置。
        </p>
        {keyMsg ? <p className="mt-2 text-sm text-slate-700">{keyMsg}</p> : null}
        <div className="mt-3 flex max-w-xl flex-col gap-2 sm:flex-row sm:items-end">
          <label className={`${labelClass} min-w-0 flex-1`}>
            <span>密钥（明文仅在此处展示）</span>
            <input
              className={`${inputClass} font-mono text-sm`}
              value={androidKey}
              onChange={(e) => setAndroidKey(e.target.value)}
              placeholder="留空表示未配置"
              spellCheck={false}
              autoComplete="off"
              maxLength={255}
              disabled={keySaving}
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            className="shrink-0 sm:mb-0.5"
            disabled={keySaving}
            onClick={fillRandomAndroidRobotKey}
          >
            生成随机密钥
          </Button>
        </div>
        <div className="mt-3">
          <Button type="button" variant="primary" disabled={keySaving} onClick={() => void saveAndroidKey()}>
            {keySaving ? '保存中…' : '保存密钥'}
          </Button>
        </div>
      </Card>

      <Card title="站点（转发策略 / 队列）">
        <p className="mb-3 text-sm text-slate-600">
          以下配置仅保存在<strong>本小程序</strong>，与站点管理中的全局机器人设置、以及其他小程序互不影响。未单独配置的站点默认为「转发关闭 + 管理员挑选」。一级站点可查看本小程序下聚合队列（含下属二级）。
        </p>
        {loading ? (
          <Spinner label="加载中" />
        ) : sites.length === 0 ? (
          <p className="text-sm text-amber-800">
            当前小程序未关联站点，或站点不在可见范围内。请先在「站点管理」中创建/关联站点后再配置机器人。
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-2 pr-4 font-medium">站点</th>
                  <th className="py-2 pr-4 font-medium">模块</th>
                  <th className="py-2 pr-4 font-medium">策略</th>
                  <th className="py-2 pr-4 font-medium">转发类型</th>
                  <th className="py-2 pr-4 font-medium">队列</th>
                  <th className="py-2 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {siteDisplayRows.map(({ site: s, depth, subline }) => {
                  const sum = summaryById[s.id];
                  const on = sum?.robot_module_enabled ?? false;
                  const strat = sum?.forward_strategy ?? 'curated';
                  const dailyCap = typeof sum?.forward_daily_cap === 'number' ? sum.forward_daily_cap : 10;
                  const directQ = sum?.forward_task_count ?? 0;
                  const aggQ = sum?.forward_task_count_aggregate ?? directQ;
                  const inheriting = !!sum?.inherit?.using_parent_minipro_settings;
                  const parentLabel = sum?.inherit?.parent_site_name?.trim();
                  const src = sum?.robot_settings_source ?? 'miniprogram_default';
                  const isDefault = src === 'miniprogram_default';

                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-slate-100 ${depth === 1 ? 'bg-emerald-50/30' : ''}`}
                    >
                      <td className={`py-2 pr-4 ${depth === 1 ? 'border-l-2 border-l-emerald-400/80 pl-6' : ''}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          {depth === 0 ? (
                            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                              一级
                            </span>
                          ) : (
                            <span className="rounded bg-emerald-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                              二级
                            </span>
                          )}
                          <div>
                            <div className="font-medium text-slate-900">{s.site_name}</div>
                            <div className="text-xs text-slate-500">
                              #{s.id}
                              {s.site_code ? ` · ${s.site_code}` : ''}
                            </div>
                            {subline ? <p className="mt-0.5 text-[11px] text-emerald-800/90">{subline}</p> : null}
                            {isDefault ? (
                              <p className="mt-0.5 text-[11px] text-slate-500">本小程序未单独配置（默认关闭转发）</p>
                            ) : inheriting && parentLabel ? (
                              <p className="mt-0.5 text-[11px] text-sky-800">
                                沿用本小程序内一级站「{parentLabel}」的策略
                              </p>
                            ) : depth === 1 && !inheriting ? (
                              <p className="mt-0.5 text-[11px] text-emerald-800">本小程序内已单独配置</p>
                            ) : src === 'miniprogram_override' ? (
                              <p className="mt-0.5 text-[11px] text-emerald-800">本小程序内已单独配置</p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        {on ? (
                          <span className="text-emerald-700">转发已开启</span>
                        ) : (
                          <span className="text-slate-500">转发已关闭</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <span>{strat === 'auto' ? '新信息自动转发' : '管理员挑选转发'}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">每日上限 {dailyCap} 条/站点</span>
                      </td>
                      <td className="py-2 pr-4 text-xs text-slate-600">
                        {(() => {
                          const fwdKeys = sum?.forward_type_keys;
                          if (fwdKeys === null || fwdKeys === undefined) {
                            return <span className="text-slate-700">全部类型</span>;
                          }
                          if (!fwdKeys.length) {
                            return <span className="text-amber-800">不自动转发任何类型</span>;
                          }
                          const labels = fwdKeys.map((k) => {
                            const hit = mpTypes.find((t) => t.key === k);
                            return hit?.label || k;
                          });
                          return (
                            <span title={labels.join('、')}>
                              {labels.slice(0, 2).join('、')}
                              {labels.length > 2 ? ` 等 ${labels.length} 类` : ''}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2 pr-4">
                        {depth === 0 && aggQ > directQ ? (
                          <>
                            <span className="font-medium text-slate-800">{aggQ}</span>
                            <span className="text-slate-600"> 条</span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              本站 {directQ} + 二级 {aggQ - directQ}
                            </span>
                          </>
                        ) : (
                          <span>{directQ} 条</span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs" onClick={() => openStrategy(s)}>
                            转发策略
                          </Button>
                          <Link to={`/robot-miniprograms/${mpId}/sites/${s.id}/tasks`}>
                            <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs">
                              转发任务
                            </Button>
                          </Link>
                          <Button
                            type="button"
                            variant={on ? 'danger' : 'primary'}
                            className="!px-2 !py-1 text-xs"
                            onClick={() => void toggleModule(s.id, !on)}
                          >
                            {on ? '关闭转发' : '开启转发'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {strategySite ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              转发策略 · {strategySite.site_name}
              {strategySite.parent_site_id ? (
                <span className="ml-1 text-sm font-normal text-emerald-700">（二级站点）</span>
              ) : null}
            </h3>
            {strategyInherit?.using_parent_minipro_settings && strategyInherit.parent_site_name ? (
              <p className="mt-2 rounded-lg border border-sky-200/90 bg-sky-50 px-3 py-2 text-xs text-sky-950">
                当前沿用本小程序内一级站「{strategyInherit.parent_site_name}」的策略。保存后将为本站在本小程序内的独立配置。
              </p>
            ) : strategySite.parent_site_id && strategyInherit?.parent_site_name ? (
              <p className="mt-2 rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                本站已在本小程序内单独配置（不再沿用「{strategyInherit.parent_site_name}」）。
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                仅影响<strong>本小程序</strong>下该站点的转发行为；与其他小程序无关。
              </p>
            )}
            <div className="mt-4 space-y-3">
              <label className={`${labelClass} flex cursor-pointer items-start gap-2`}>
                <input type="radio" name="fwd" checked={strategyChoice === 'auto'} onChange={() => setStrategyChoice('auto')} className="mt-1" />
                <span>
                  <span className="font-medium text-slate-800">新信息自动转发</span>
                  <span className="mt-0.5 block text-xs text-slate-500">新登记信息在满足条件时自动进入转发队列（受每日上限约束）。</span>
                </span>
              </label>
              <label className={`${labelClass} flex cursor-pointer items-start gap-2`}>
                <input type="radio" name="fwd" checked={strategyChoice === 'curated'} onChange={() => setStrategyChoice('curated')} className="mt-1" />
                <span>
                  <span className="font-medium text-slate-800">管理员挑选转发</span>
                  <span className="mt-0.5 block text-xs text-slate-500">仅手动加入转发队列的信息会被 Android 机器人拉取。</span>
                </span>
              </label>
              <label className={labelClass}>
                <span>每日转发上限（条）</span>
                <input
                  type="number"
                  min={1}
                  max={500}
                  className={inputClass}
                  value={strategyDailyCap}
                  onChange={(e) => setStrategyDailyCap(Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 10)))}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  自动转发模式下，单站点每日最多入队条数；管理员挑选模式不受此限用于手动入队。默认 10。
                </span>
              </label>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-sm font-medium text-slate-800">转发信息类型</p>
                <p className="mt-1 text-xs text-slate-500">
                  仅影响「新信息自动转发」的入队范围；未勾选的类型不会自动进入转发队列，但可在转发任务页手动添加。
                </p>
                <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={strategyForwardAllTypes}
                    onChange={(e) => setStrategyForwardAllTypesEnabled(e.target.checked)}
                  />
                  <span>全部信息类型</span>
                </label>
                {mpTypes.length > 0 ? (
                  <div className={`mt-2 flex flex-wrap gap-2 ${strategyForwardAllTypes ? 'opacity-50' : ''}`}>
                    {mpTypes.map((t) => {
                      const checked = strategyForwardTypeKeys.includes(t.key);
                      return (
                        <label
                          key={t.key}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${
                            checked && !strategyForwardAllTypes
                              ? 'border-teal-500 bg-teal-50 text-teal-900'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            disabled={strategyForwardAllTypes}
                            checked={checked}
                            onChange={() => toggleStrategyForwardType(t.key)}
                          />
                          <span>{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-amber-800">请先在「小程序管理 → 信息类型」中配置类型后再筛选。</p>
                )}
                {!strategyForwardAllTypes && strategyForwardTypeKeys.length === 0 ? (
                  <p className="mt-2 text-xs text-amber-800">未选择任何类型时，自动转发将不会入队任何信息（仍可手动添加）。</p>
                ) : null}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {strategySite.parent_site_id && !strategyInherit?.robot_inherit_parent ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={strategySaving}
                  onClick={() => void restoreRobotInherit(strategySite.id)}
                >
                  恢复继承一级
                </Button>
              ) : null}
              <Button type="button" variant="secondary" onClick={() => setStrategySite(null)} disabled={strategySaving}>
                取消
              </Button>
              <Button type="button" variant="primary" onClick={() => void saveStrategy()} disabled={strategySaving}>
                {strategySaving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
